import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { useLiveData } from "@/lib/useLiveData";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useAuth } from "@/context/AuthContext";
import { formatXCD } from "@/lib/constants";
import { Wrench } from "lucide-react";

export default function DiagnosticsPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    vehicle_type: "car", vehicle_make: "", vehicle_model: "",
    appointment_date: "", contact_phone: "", notes: "",
  });
  const [prices, setPrices] = useState({ car: 0, truck: 0 });
  const [s, setS] = useState(false);

  useLiveData(() => {
    api.get("/pricing").then(r => setPrices(r.data.diagnostics || {})).catch(() => {});
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast("Sign in to book"); return nav("/login"); }
    setS(true);
    try {
      const { data } = await api.post("/bookings/diagnostics", form);
      toast.success(`Diagnostic booked — ${data.invoice_number}`);
      nav("/dashboard");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setS(false); }
  };

  return (
    <Layout>
      <section className="relative bg-gradient-to-br from-[#1D4ED8] via-[#1E40AF] to-[#0F172A]">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-white">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#FFCE00]">Service · Vehicle Diagnostics</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl mt-2">Know exactly what's wrong.</h1>
          <p className="mt-3 max-w-xl text-white/90">Honest diagnostics from EC$200 for cars and EC$250 for trucks.</p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white border border-slate-200 rounded-md p-6 sm:p-8">
          <div className="flex items-center gap-2 text-[#1D4ED8]"><Wrench className="h-5 w-5" strokeWidth={2.5} /><span className="tag-overline">Book now</span></div>
          <h2 className="font-heading font-black text-2xl mt-1">Diagnostics appointment</h2>
          <form onSubmit={submit} className="mt-6 space-y-5">
            <div>
              <Label className="text-sm font-bold text-slate-900 mb-2 block">Vehicle type</Label>
              <RadioGroup value={form.vehicle_type} onValueChange={(v) => setForm({ ...form, vehicle_type: v })} className="grid grid-cols-2 gap-3">
                {["car", "truck"].map((t) => (
                  <label key={t} data-testid={`diag-type-${t}`}
                         className={`cursor-pointer border-2 rounded-md p-4 text-center transition ${form.vehicle_type === t ? "border-[#E32636] bg-red-50" : "border-slate-200"}`}>
                    <RadioGroupItem value={t} className="sr-only" />
                    <div className="font-heading font-black text-xl text-slate-900 capitalize">{t}</div>
                    <div className="text-sm font-bold text-[#1D4ED8]">{formatXCD(prices[t])}</div>
                  </label>
                ))}
              </RadioGroup>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-bold text-slate-900">Make</Label>
                <Input data-testid="diag-make" required value={form.vehicle_make} onChange={e => setForm({ ...form, vehicle_make: e.target.value })} />
              </div>
              <div>
                <Label className="text-sm font-bold text-slate-900">Model</Label>
                <Input data-testid="diag-model" required value={form.vehicle_model} onChange={e => setForm({ ...form, vehicle_model: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-bold text-slate-900">Preferred date</Label>
                <Input data-testid="diag-date" type="date" required value={form.appointment_date} onChange={e => setForm({ ...form, appointment_date: e.target.value })} />
              </div>
              <div>
                <Label className="text-sm font-bold text-slate-900">Contact phone</Label>
                <Input data-testid="diag-phone" required value={form.contact_phone} onChange={e => setForm({ ...form, contact_phone: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-sm font-bold text-slate-900">Notes</Label>
              <Textarea data-testid="diag-notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </div>
            <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-md p-4">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Total</div>
                <div className="font-heading font-black text-3xl text-slate-900" data-testid="diag-total">{formatXCD(prices[form.vehicle_type])}</div>
              </div>
              <Button type="submit" disabled={s} data-testid="diag-submit-btn"
                      className="bg-[#E32636] hover:bg-[#C91F2C] text-white font-bold h-12 px-6">
                {s ? "Booking…" : "Confirm appointment"}
              </Button>
            </div>
          </form>
        </div>
      </section>
    </Layout>
  );
}
